/**
 * Centralized Role-Based Access Control (RBAC) Configuration
 * 
 * This module mirrors the backend roles.py structure for frontend consistency.
 * When adding new roles/permissions to the backend, update this file to match.
 * 
 * @see seapattern_backend/src/auth/roles.py
 */

// ============================================================================
// ROLES - Must match backend Role enum
// ============================================================================
export const Role = {
    USER: 'user',
    ADMIN: 'admin',
    ENGINEER: 'engineer',
    EXECUTIVE: 'executive',
} as const;

export type RoleType = (typeof Role)[keyof typeof Role];

// ============================================================================
// PERMISSIONS - Must match backend Permission enum
// ============================================================================
export const Permission = {
    // Video permissions
    VIDEOS_READ: 'videos:read',
    VIDEOS_WRITE: 'videos:write',
    VIDEOS_DELETE: 'videos:delete',
    VIDEOS_READ_ALL: 'videos:read_all',

    // User management permissions
    USERS_READ: 'users:read',
    USERS_WRITE: 'users:write',
    USERS_DELETE: 'users:delete',

    // System/admin permissions
    SYSTEM_READ: 'system:read',
    SYSTEM_WRITE: 'system:write',

    // Hydropower/analysis permissions
    HYDROPOWER_READ: 'hydropower:read',
    HYDROPOWER_WRITE: 'hydropower:write',

    // Site survey permissions
    SITE_SURVEY_READ: 'site_survey:read',
    SITE_SURVEY_WRITE: 'site_survey:write',

    // Simulation permissions
    SIMULATIONS_READ: 'simulations:read',
    SIMULATIONS_WRITE: 'simulations:write',

    // Reports permissions
    REPORTS_READ: 'reports:read',
    REPORTS_WRITE: 'reports:write',

    // Audit permissions
    AUDIT_READ: 'audit:read',

    // S3/Storage permissions
    STORAGE_READ: 'storage:read',
    STORAGE_WRITE: 'storage:write',

    // Monitoring/Grafana permissions
    MONITORING_READ: 'monitoring:read',
    MONITORING_WRITE: 'monitoring:write',
} as const;

export type PermissionType = (typeof Permission)[keyof typeof Permission];

// ============================================================================
// ROLE LABELS - Human-readable names for UI display
// ============================================================================
export const RoleLabels: Record<RoleType, string> = {
    [Role.USER]: 'User',
    [Role.ADMIN]: 'Administrator',
    [Role.ENGINEER]: 'Engineer',
    [Role.EXECUTIVE]: 'Executive',
};

// ============================================================================
// ROLE DESCRIPTIONS - For tooltips and help text
// ============================================================================
export const RoleDescriptions: Record<RoleType, string> = {
    [Role.USER]: 'Standard user with access to own videos and simulations',
    [Role.ADMIN]: 'Full system access including user management',
    [Role.ENGINEER]: 'Engineering access with ability to view all site data',
    [Role.EXECUTIVE]: 'Executive view access to hydropower data only',
};

// ============================================================================
// FALLBACK ROLE-PERMISSION MAPPING (for JWT fallback when API unavailable)
// This should mirror backend ROLE_PERMISSIONS but is only used as fallback
// ============================================================================
export const ROLE_PERMISSIONS_FALLBACK: Record<RoleType, PermissionType[]> = {
    [Role.USER]: [
        Permission.VIDEOS_READ,
        Permission.VIDEOS_WRITE,
        Permission.VIDEOS_DELETE,
        Permission.SIMULATIONS_READ,
        Permission.SIMULATIONS_WRITE,
        Permission.REPORTS_READ,
        Permission.REPORTS_WRITE,
        Permission.MONITORING_READ,
        Permission.SITE_SURVEY_READ,
    ],
    [Role.ENGINEER]: [
        Permission.VIDEOS_READ,
        Permission.VIDEOS_WRITE,
        Permission.VIDEOS_READ_ALL,
        Permission.SIMULATIONS_READ,
        Permission.SIMULATIONS_WRITE,
        Permission.HYDROPOWER_READ,
        Permission.HYDROPOWER_WRITE,
        Permission.SITE_SURVEY_READ,
        Permission.SITE_SURVEY_WRITE,
        Permission.SYSTEM_READ,
        Permission.MONITORING_READ,
    ],
    [Role.EXECUTIVE]: [
        Permission.HYDROPOWER_READ,
        Permission.MONITORING_READ,
    ],
    [Role.ADMIN]: Object.values(Permission), // Admin gets all permissions
};

export interface NavItem {
    title: string;
    path: string;
    requiredPermission?: PermissionType;
    allowedRoles?: RoleType[];
    requireAll?: boolean;
}

/** Main navigation items - visible based on permissions */
export const MAIN_NAV_ITEMS: NavItem[] = [
    { title: 'Dashboard', path: '/dashboard', allowedRoles: [Role.USER, Role.ENGINEER, Role.ADMIN] },
    { title: 'Site Registration', path: '/upload', requiredPermission: Permission.VIDEOS_WRITE },
    { title: 'View Analysis', path: '/analysis', requiredPermission: Permission.VIDEOS_READ },
    { title: 'Turbine Simulation', path: '/simulation', requiredPermission: Permission.SIMULATIONS_READ },
    { title: 'Hydraulic Analysis', path: '/hydraulic', requiredPermission: Permission.SIMULATIONS_READ },
    { title: 'Reports', path: '/reports', requiredPermission: Permission.REPORTS_READ },
    { title: 'Performance Center', path: '/monitoring', requiredPermission: Permission.MONITORING_READ },
    { title: 'Fleet Management', path: '/fleet', requiredPermission: Permission.MONITORING_READ, allowedRoles: [Role.USER, Role.ENGINEER, Role.ADMIN] },
    { title: 'Site Data', path: '/site-data', requiredPermission: Permission.SITE_SURVEY_READ },
    { title: 'Profile', path: '/profile' },
];

/** Admin navigation items - more restricted */
export const ADMIN_NAV_ITEMS: NavItem[] = [
    { title: 'System Dashboard', path: '/admin/dashboard', requiredPermission: Permission.SYSTEM_READ, allowedRoles: [Role.ADMIN] },
    { title: 'User Management', path: '/admin/users', requiredPermission: Permission.USERS_READ },
    { title: 'Site Data', path: '/admin/users-videos', requiredPermission: Permission.VIDEOS_READ_ALL },
    { title: 'Video Management', path: '/admin/videos', requiredPermission: Permission.VIDEOS_READ_ALL, allowedRoles: [Role.ADMIN] },
    { title: 'S3 Storage', path: '/admin/s3', requiredPermission: Permission.STORAGE_READ },
    { title: 'Site Survey', path: '/admin/site-survey', requiredPermission: Permission.SITE_SURVEY_READ },
    { title: 'Hydro Power Plants', path: '/admin/hydropower', requiredPermission: Permission.HYDROPOWER_READ },
    { title: 'Sites Assessment', path: '/admin/site-ranking', requiredPermission: Permission.HYDROPOWER_READ },
    { title: 'Site Analysis', path: '/admin/site-analysis', requiredPermission: Permission.HYDROPOWER_READ },
    { title: 'Site Potential', path: '/admin/hkt-calculator', requiredPermission: Permission.HYDROPOWER_READ, allowedRoles: [Role.ADMIN, Role.ENGINEER] },
    { title: 'Audit Logs', path: '/admin/audit', requiredPermission: Permission.AUDIT_READ },
    { title: 'Live View', path: '/admin/live-view', requiredPermission: Permission.MONITORING_READ, allowedRoles: [Role.ADMIN, Role.ENGINEER] },
    { title: 'Telemetry Data', path: '/admin/influxdb', requiredPermission: Permission.MONITORING_READ, allowedRoles: [Role.ADMIN, Role.ENGINEER] },
];


export function getAllRoles(): RoleType[] {
    return Object.values(Role);
}


export function isValidRole(role: string): role is RoleType {
    return Object.values(Role).includes(role as RoleType);
}

/**
 * Get human-readable label for a role
 */
export function getRoleLabel(role: RoleType): string {
    return RoleLabels[role] || role;
}

/**
 * Get permissions for a role (fallback only - prefer API response)
 */
export function getPermissionsForRole(role: RoleType): PermissionType[] {
    return ROLE_PERMISSIONS_FALLBACK[role] || [];
}

/**
 * Check if a permission array includes a specific permission
 */
export function hasPermission(permissions: string[], permission: PermissionType): boolean {
    return permissions.includes(permission);
}

/**
 * Check if a permission array includes any of the specified permissions
 */
export function hasAnyPermission(permissions: string[], requiredPermissions: PermissionType[]): boolean {
    return requiredPermissions.some(p => permissions.includes(p));
}

/**
 * Check if a permission array includes all of the specified permissions
 */
export function hasAllPermissions(permissions: string[], requiredPermissions: PermissionType[]): boolean {
    return requiredPermissions.every(p => permissions.includes(p));
}

/**
 * Filter nav items based on user's permissions
 */
export function filterNavItems(items: NavItem[], permissions: string[], role: RoleType): NavItem[] {
    return items.filter(item => {
        if (item.allowedRoles && item.allowedRoles.length > 0) {
            if (!item.allowedRoles.includes(role)) {
                return false;
            }
        }

        // If a permission is required, check for it
        if (item.requiredPermission) {
            return permissions.includes(item.requiredPermission);
        }

        // No restrictions - show item
        return true;
    });
}
